import { useMsal } from '@azure/msal-react';
import { useCallback } from 'react';
import { useRecoilValue } from 'recoil';
import { DicomColor } from '~/domain/dicom/dicomColor';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import {
    ROI_POINT_TYPES,
    RoiIdentificationCodeSequence,
    RoiInterpretedType,
} from '~/domain/dicom/dicomstructureset';
import {
    domainEventBus,
    RoiApprovalStatusChangeRequestedEvent,
    RoiColorChangeRequestedEvent,
    RoiContourThicknessChangeRequestedEvent,
    RoiCopyRequestedEvent,
    RoiElectronDensityOverrideChangeRequestedEvent,
    RoiMassDensityOverrideChangeRequestedEvent,
    RoiDescriptionChangeRequestedEvent,
    RoiInterpretedTypeChangeRequestedEvent,
    RoiNameChangeRequestedEvent,
    RoiPointCoordinatesChangeRequestedEvent,
    RoiSdfCopyOverRequestedEvent,
    RoiSelectionRequestedEvent,
    RoiVisibilityChangeRequestedEvent,
} from '~/domain/events';
import { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import { environmentState } from '~/state/atoms/engineAtoms';
import { sessionDataAtom } from '~/state/atoms/sessionAtoms';

export const formatIdentificationCode = (
    code: RoiIdentificationCodeSequence | undefined
) => {
    return code
        ? `${code.codeValue} ${code.codingSchemeDesignator} ${code.codeMeaning}`
        : '';
};

/**
 * Hook for managing ROI operations via domain events.
 *
 * Provides functions to modify ROI properties by publishing domain events
 * to the event bus. All operations are handled asynchronously through the event-driven architecture.
 * The actual state updates are performed by event handlers, maintaining separation of concerns.
 *
 * @param roiInstance The ROI instance to operate on
 * @returns ROI domain event publishing functions.
 */
const useRoi = (roiInstance: RoiReadModel) => {
    const { instance } = useMsal();
    const sessionData = useRecoilValue(sessionDataAtom);
    const env = useRecoilValue(environmentState);

    const getRoiVolumeCm3 = useCallback(() => {
        return env?.getRoiVolumeCm3(roiInstance.roiNumber) ?? Number.NaN;
    }, [env, roiInstance.roiNumber]);

    const isRoiEmpty = useCallback(() => {
        return env?.isRoiEmpty(roiInstance.roiNumber) ?? true;
    }, [env, roiInstance.roiNumber]);

    const getRoiEquivalentSphereDiameterCm = useCallback(() => {
        return 2 * Math.cbrt((3 * getRoiVolumeCm3()) / (4 * Math.PI));
    }, [getRoiVolumeCm3]);

    const setVisible = useCallback(
        async (visible: boolean) => {
            try {
                await domainEventBus.publish(
                    new RoiVisibilityChangeRequestedEvent(
                        roiInstance.roiNumber,
                        visible,
                        'user'
                    )
                );
            } catch (error) {
                console.error(`Error updating ROI visibility: ${error}`);
            }
        },
        [roiInstance.roiNumber]
    );

    const setName = useCallback(
        async (newName: string) => {
            try {
                await domainEventBus.publish(
                    new RoiNameChangeRequestedEvent(
                        roiInstance.roiNumber,
                        newName,
                        'user'
                    )
                );
            } catch (error) {
                console.error(`Error updating ROI name: ${error}`);
            }
        },
        [roiInstance.roiNumber]
    );

    const setColor = useCallback(
        async (newColor: DicomColor) => {
            try {
                await domainEventBus.publish(
                    new RoiColorChangeRequestedEvent(
                        roiInstance.roiNumber,
                        newColor,
                        'user'
                    )
                );
            } catch (error) {
                console.error(`Error updating ROI color: ${error}`);
            }
        },
        [roiInstance.roiNumber]
    );

    const setContourThickness = useCallback(
        async (newThickness: number) => {
            try {
                await domainEventBus.publish(
                    new RoiContourThicknessChangeRequestedEvent(
                        roiInstance.roiNumber,
                        newThickness,
                        'user'
                    )
                );
            } catch (error) {
                console.error(`Error updating ROI contour thickness: ${error}`);
            }
        },
        [roiInstance.roiNumber]
    );

    const setRoiApprovalStatus = useCallback(
        async (newStatus: DICOMApprovalStatus, newReviewerName?: string) => {
            const activeMsalAccount = instance?.getActiveAccount()?.name;
            const reviewer =
                newReviewerName ??
                sessionData?.username ??
                activeMsalAccount ??
                'unknown';

            try {
                await domainEventBus.publish(
                    new RoiApprovalStatusChangeRequestedEvent(
                        roiInstance.roiNumber,
                        newStatus,
                        reviewer,
                        'user'
                    )
                );
            } catch (error) {
                console.error(`Error updating ROI approval status: ${error}`);
            }
        },
        [roiInstance.roiNumber, sessionData, instance]
    );

    const setPointCoordinates = useCallback(
        async (coordinates: { x: number; y: number; z: number }) => {
            try {
                await domainEventBus.publish(
                    new RoiPointCoordinatesChangeRequestedEvent(
                        roiInstance.roiNumber,
                        coordinates,
                        'user'
                    )
                );
            } catch (error) {
                console.error(`Error updating ROI point coordinates: ${error}`);
            }
        },
        [roiInstance.roiNumber]
    );

    const setInterpretedType = useCallback(
        async (newType: RoiInterpretedType) => {
            try {
                await domainEventBus.publish(
                    new RoiInterpretedTypeChangeRequestedEvent(
                        roiInstance.roiNumber,
                        newType,
                        'user'
                    )
                );

                // When switching from a volumetric type to a point type,
                // auto-place the point at the current crosshair position so
                // the user never has to manually set an initial coordinate.
                const wasPointType = ROI_POINT_TYPES.includes(
                    roiInstance.interpretedType
                );
                const isNowPointType = ROI_POINT_TYPES.includes(newType);
                if (!wasPointType && isNowPointType) {
                    const coords = env?.planeIntersectionPoint.coordinates;
                    if (coords) {
                        await setPointCoordinates({
                            x: coords[0],
                            y: coords[1],
                            z: coords[2],
                        });
                    }
                }
            } catch (error) {
                console.error(`Error updating ROI interpreted type: ${error}`);
            }
        },
        [
            roiInstance.roiNumber,
            roiInstance.interpretedType,
            env,
            setPointCoordinates,
        ]
    );

    const setElectronDensityOverride = useCallback(
        async (newDensity: number | undefined) => {
            try {
                await domainEventBus.publish(
                    new RoiElectronDensityOverrideChangeRequestedEvent(
                        roiInstance.roiNumber,
                        newDensity,
                        'user'
                    )
                );
            } catch (error) {
                console.error(
                    `Error updating ROI electron density override: ${error}`
                );
            }
        },
        [roiInstance.roiNumber]
    );

    const setMassDensityOverride = useCallback(
        async (newDensity: number | undefined) => {
            try {
                await domainEventBus.publish(
                    new RoiMassDensityOverrideChangeRequestedEvent(
                        roiInstance.roiNumber,
                        newDensity,
                        'user'
                    )
                );
            } catch (error) {
                console.error(
                    `Error updating ROI mass density override: ${error}`
                );
            }
        },
        [roiInstance.roiNumber]
    );

    const setRoiDescription = useCallback(
        async (newDescription: string) => {
            try {
                await domainEventBus.publish(
                    new RoiDescriptionChangeRequestedEvent(
                        roiInstance.roiNumber,
                        newDescription,
                        'user'
                    )
                );
            } catch (error) {
                console.error(`Error updating ROI description: ${error}`);
            }
        },
        [roiInstance.roiNumber]
    );

    const setRoiSelected = useCallback(async () => {
        try {
            await domainEventBus.publish(
                new RoiSelectionRequestedEvent(roiInstance.roiNumber, 'user')
            );
        } catch (error) {
            console.error(`Error selecting ROI: ${error}`);
        }
    }, [roiInstance.roiNumber]);

    const copyRoi = useCallback(
        async (structureSetUid: string) => {
            try {
                await domainEventBus.publish(
                    new RoiCopyRequestedEvent(
                        structureSetUid,
                        roiInstance.roiNumber,
                        'user'
                    )
                );
            } catch (error) {
                console.error(`Error copying ROI: ${error}`);
            }
        },
        [roiInstance.roiNumber]
    );

    const copySdfOver = useCallback(
        async (structureSetUid: string, targetRoiNumber: number) => {
            try {
                await domainEventBus.publish(
                    new RoiSdfCopyOverRequestedEvent(
                        structureSetUid,
                        roiInstance.roiNumber,
                        targetRoiNumber,
                        'user'
                    )
                );
            } catch (error) {
                console.error(`Error copying SDF over: ${error}`);
            }
        },
        [roiInstance.roiNumber]
    );

    return {
        getRoiVolumeCm3,
        isRoiEmpty,
        getRoiEquivalentSphereDiameterCm,
        setVisible,
        setName,
        setColor,
        setContourThickness,
        setRoiApprovalStatus,
        setInterpretedType,
        setElectronDensityOverride,
        setMassDensityOverride,
        setPointCoordinates,
        setRoiDescription,
        setRoiSelected,
        copyRoi,
        copySdfOver,
    };
};

export default useRoi;
